import { Component } from '@theme/component';

/**
 * media-slider-component — port Horizon du « splide-component » Shrine.
 * Moteur maison (translateX), sans dépendance externe.
 *
 * Attributs (data-*) :
 *  type: slide | loop | fade          drag: true | false | free
 *  autoplay: true|false               autoplay-speed: secondes
 *  focus: center (mode centré)        trim-space: false (autorise le vide aux bords en mode centré)
 *  omit-end: true (pas de page « vide » à la fin)
 *  slides-desktop / slides-mobile     per-move-desktop / per-move-mobile
 *  gap-desktop / gap-mobile (px)      side-padding-desktop / side-padding-mobile (px)
 *  padding-calc-desktop / -mobile ("true" = pas de vide avant la 1re / après la dernière slide)
 *  adaptive-height-desktop / -mobile  pause-videos: true
 *  destroy-desktop / destroy-mobile: "true" = le slider est désactivé sur ce breakpoint
 *    (port du data-destroy-* Shrine) : pas de clones, pas de drag, pas de contrôles ;
 *    la classe `ms--destroyed` est posée sur la racine pour que la section affiche une grille CSS.
 *
 * Structure attendue :
 *  <media-slider-component>
 *    <div class="ms" ref="root">
 *      <div class="ms__track" ref="track"><ul class="ms__list" ref="list"><li class="ms__slide">…</li></ul></div>
 *      <div class="ms__controls"><ul class="ms__dots" ref="dots"></ul><div class="ms__arrows" ref="arrows"></div></div>
 *    </div>
 *  </media-slider-component>
 */

const DESKTOP = window.matchMedia('(min-width: 750px)');
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

const ARROW_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="40" height="40" focusable="false" aria-hidden="true"><path d="m15.5 0.932-4.3 4.38 14.5 14.6-14.5 14.5 4.3 4.4 14.6-14.6 4.4-4.3-4.4-4.4-14.6-14.6z"/></svg>';

class MediaSliderComponent extends Component {
  requiredRefs = ['root', 'track', 'list'];

  /** @type {HTMLElement[]} */
  #originals = [];
  /** @type {HTMLElement[]} */
  #all = [];
  #cloneCount = 0;
  #index = 0; // index dans #all (loop) ou dans #originals
  #position = 0; // translateX courant (px, négatif)
  #autoplayTimer = 0;
  #paused = false;
  #hovered = false;
  #resizeObserver = null;
  #intersection = null;
  #inViewport = true;
  #drag = null;
  #settling = false;
  #onMoveEnd = null;

  connectedCallback() {
    super.connectedCallback();
    this.#originals = Array.from(this.refs.list.children).filter(
      (el) => el instanceof HTMLElement && !el.classList.contains('ms__slide--clone')
    );
    this.#originals.forEach((s, i) => {
      s.dataset.index = String(i);
      s.classList.add('ms__slide');
    });
    this.#bind();
    this.#setup();
    this.refs.root.classList.add('is-initialized');
  }

  /** Construit le slider, ou le démonte si le breakpoint courant est « destroy » */
  #setup() {
    if (this.destroyed) {
      this.#teardown();
      return;
    }
    this.refs.root.classList.remove('ms--destroyed');
    this.#build();
    this.#layout(true);
    this.#updateControls();
    this.#startAutoplay();
  }

  /** État « grille » : aucun comportement slider sur ce breakpoint */
  #teardown() {
    this.#stopAutoplay();
    this.#drag = null;
    this.refs.list.querySelectorAll('.ms__slide--clone').forEach((c) => c.remove());
    this.#cloneCount = 0;
    this.#all = this.#originals.slice();
    this.#index = 0;
    this.#position = 0;
    const root = this.refs.root;
    root.classList.remove('ms--fade', 'ms--loop', 'ms--draggable', 'ms--center', 'ms--adaptive', 'is-overflow', 'is-dragging');
    root.classList.add('ms--destroyed');
    ['--ms-columns', '--ms-gap', '--ms-pad-left', '--ms-pad-right'].forEach((p) => root.style.removeProperty(p));
    this.style.removeProperty('--active-slide-height');
    this.refs.list.style.transform = '';
    this.refs.list.style.transition = '';
    if (this.refs.arrows) this.refs.arrows.innerHTML = '';
    if (this.refs.dots) this.refs.dots.innerHTML = '';
    this.#all.forEach((s) => {
      s.classList.remove('is-active', 'is-visible');
      s.removeAttribute('aria-hidden');
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopAutoplay();
    this.#resizeObserver?.disconnect();
    this.#intersection?.disconnect();
    DESKTOP.removeEventListener('change', this.#onBreakpoint);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  /* ---------- config ---------- */

  get type() {
    const t = this.dataset.type || 'slide';
    return this.#originals.length < 2 ? 'slide' : t;
  }

  get isLoop() {
    return this.type === 'loop';
  }

  get isFade() {
    return this.type === 'fade';
  }

  get dragMode() {
    const d = this.dataset.drag;
    if (d === 'false') return 'none';
    if (d === 'free') return 'free';
    return 'snap';
  }

  get isDesktop() {
    return DESKTOP.matches;
  }

  /** true si le slider est désactivé sur le breakpoint courant (data-destroy-desktop / data-destroy-mobile) */
  get destroyed() {
    return this.isDesktop ? this.dataset.destroyDesktop === 'true' : this.dataset.destroyMobile === 'true';
  }

  #num(key, fallback) {
    const suffix = this.isDesktop ? 'Desktop' : 'Mobile';
    const v = parseFloat(this.dataset[key + suffix] ?? '');
    return Number.isFinite(v) ? v : fallback;
  }

  get perPage() {
    return this.isFade ? 1 : Math.max(1, Math.round(this.#num('slides', 1)));
  }

  get perMove() {
    return Math.max(1, Math.round(this.#num('perMove', 1)));
  }

  get gap() {
    return this.isFade ? 0 : this.#num('gap', 0);
  }

  get sidePadding() {
    return this.isFade ? 0 : this.#num('sidePadding', 0);
  }

  get paddingCalc() {
    const suffix = this.isDesktop ? 'Desktop' : 'Mobile';
    return this.dataset['paddingCalc' + suffix] === 'true';
  }

  get adaptiveHeight() {
    const suffix = this.isDesktop ? 'Desktop' : 'Mobile';
    return (
      !this.destroyed && this.dataset['adaptiveHeight' + suffix] === 'true' && this.perPage === 1 && this.dragMode !== 'free'
    );
  }

  get centerMode() {
    return this.dataset.focus === 'center' && !this.isFade;
  }

  get trimSpace() {
    return this.dataset.trimSpace !== 'false';
  }

  get omitEnd() {
    return this.dataset.omitEnd !== 'false';
  }

  get autoplay() {
    return this.dataset.autoplay === 'true' && this.#originals.length > 1;
  }

  get interval() {
    const s = parseFloat(this.dataset.autoplaySpeed ?? '5');
    return (Number.isFinite(s) && s > 0 ? s : 5) * 1000;
  }

  get count() {
    return this.#originals.length;
  }

  /* ---------- structure ---------- */

  #build() {
    // Nettoie d'anciens clones (re-render éditeur)
    this.refs.list.querySelectorAll('.ms__slide--clone').forEach((c) => c.remove());
    this.#cloneCount = 0;

    if (this.isLoop) {
      const n = this.count;
      const needed = Math.min(n, this.perPage + this.perMove + 1);
      this.#cloneCount = needed;
      const before = [];
      const after = [];
      for (let i = 0; i < needed; i++) {
        const src = this.#originals[(n - needed + i) % n];
        const c = this.#clone(src);
        before.push(c);
      }
      for (let i = 0; i < needed; i++) {
        const c = this.#clone(this.#originals[i % n]);
        after.push(c);
      }
      before.forEach((c) => this.refs.list.insertBefore(c, this.#originals[0]));
      after.forEach((c) => this.refs.list.appendChild(c));
      this.#all = Array.from(this.refs.list.children);
      this.#index = this.#cloneCount;
    } else {
      this.#all = this.#originals.slice();
      this.#index = Math.min(this.#index, this.#maxIndex);
    }

    this.refs.root.classList.toggle('ms--fade', this.isFade);
    this.refs.root.classList.toggle('ms--loop', this.isLoop);
    this.refs.root.classList.toggle('ms--draggable', this.dragMode !== 'none' && !this.isFade);
    this.refs.root.classList.toggle('ms--center', this.centerMode);
    this.refs.root.classList.toggle('is-overflow', this.count > this.perPage || this.isFade || this.isLoop);

    this.#buildArrows();
    this.#buildDots();
  }

  #clone(src) {
    const c = /** @type {HTMLElement} */ (src.cloneNode(true));
    c.classList.add('ms__slide--clone');
    c.setAttribute('aria-hidden', 'true');
    c.removeAttribute('id');
    c.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    // Les clones vidéo ne doivent pas jouer : on neutralise l'autoplay
    c.querySelectorAll('internal-video-component').forEach((v) => {
      v.setAttribute('data-clone', 'true');
    });
    // Shopify : ne pas dupliquer les attributs éditeur
    c.querySelectorAll('[data-shopify-editor-block]').forEach((el) => el.removeAttribute('data-shopify-editor-block'));
    return c;
  }

  #buildArrows() {
    const wrap = this.refs.arrows;
    if (!wrap) return;
    wrap.innerHTML = '';
    if (this.count < 2) return;
    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'ms__arrow ms__arrow--prev';
    prev.setAttribute('aria-label', this.dataset.labelPrev || 'Previous slide');
    prev.innerHTML = ARROW_SVG;
    prev.addEventListener('click', () => this.previous());
    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'ms__arrow ms__arrow--next';
    next.setAttribute('aria-label', this.dataset.labelNext || 'Next slide');
    next.innerHTML = ARROW_SVG;
    next.addEventListener('click', () => this.next());
    wrap.append(prev, next);
  }

  get pageCount() {
    const n = this.count;
    if (n < 2) return 0;
    if (this.isFade || this.isLoop) return n;
    if (this.centerMode && !this.trimSpace) return n;
    const pages = this.omitEnd ? Math.ceil(Math.max(0, n - this.perPage) / this.perMove) + 1 : Math.ceil(n / this.perMove);
    return Math.max(1, pages);
  }

  #buildDots() {
    const dots = this.refs.dots;
    if (!dots) return;
    dots.innerHTML = '';
    const pages = this.pageCount;
    if (pages < 2) return;
    for (let p = 0; p < pages; p++) {
      const li = document.createElement('li');
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'ms__dot';
      b.setAttribute('aria-label', (this.dataset.labelDot || 'Go to slide [n]').replace('[n]', String(p + 1)));
      b.addEventListener('click', () => this.goToPage(p));
      li.appendChild(b);
      dots.appendChild(li);
    }
  }

  /* ---------- géométrie ---------- */

  get #trackWidth() {
    return this.refs.track.clientWidth;
  }

  get #paddingLeft() {
    return this.paddingCalc ? 0 : this.sidePadding;
  }

  get #paddingRight() {
    return this.sidePadding;
  }

  get #contentWidth() {
    return this.#trackWidth - this.#paddingLeft - this.#paddingRight;
  }

  get #slideWidth() {
    const cols = this.perPage;
    return (this.#contentWidth + this.gap) / cols - this.gap;
  }

  get #step() {
    return this.#slideWidth + this.gap;
  }

  get #maxIndex() {
    if (this.isLoop) return this.#all.length - 1;
    if (this.isFade) return this.count - 1;
    if (this.centerMode && !this.trimSpace) return this.count - 1;
    if (this.omitEnd) return Math.max(0, this.count - this.perPage);
    return this.count - 1;
  }

  /** Position (px, >=0) de la slide i alignée selon le focus */
  #positionOf(i) {
    let x = i * this.#step;
    if (this.centerMode) {
      x -= (this.#contentWidth - this.#slideWidth) / 2;
    }
    return x;
  }

  #clampPosition(x) {
    if (this.isLoop) return x;
    const listWidth = this.#all.length * this.#step - this.gap;
    let max = listWidth - this.#contentWidth;
    if (this.paddingCalc && this.#paddingRight > 0) max += this.#paddingRight; // la dernière slide touche le bord
    if (this.centerMode && !this.trimSpace) {
      return Math.min(Math.max(x, this.#positionOf(0)), this.#positionOf(this.count - 1));
    }
    return Math.min(Math.max(x, 0), Math.max(0, max));
  }

  #layout(instant = false) {
    const root = this.refs.root;
    root.style.setProperty('--ms-columns', String(this.perPage));
    root.style.setProperty('--ms-gap', `${this.gap}px`);
    root.style.setProperty('--ms-pad-left', `${this.#paddingLeft}px`);
    root.style.setProperty('--ms-pad-right', `${this.#paddingRight}px`);
    root.classList.toggle('ms--adaptive', this.adaptiveHeight);
    if (this.isFade) {
      this.#applyFade();
    } else {
      this.#moveTo(this.#index, instant);
    }
    this.#updateActive();
  }

  #applyFade() {
    this.#all.forEach((s, i) => {
      s.classList.toggle('is-active', i === this.#index);
      s.setAttribute('aria-hidden', i === this.#index ? 'false' : 'true');
    });
    this.refs.list.style.transform = '';
    this.#setAdaptiveHeight();
  }

  #moveTo(index, instant = false, onEnd = null) {
    const x = this.#clampPosition(this.#positionOf(index));
    this.#position = -x;
    const list = this.refs.list;
    if (instant || REDUCED_MOTION.matches) {
      list.style.transition = 'none';
      list.style.transform = `translate3d(${-x}px,0,0)`;
      // force reflow
      void list.offsetWidth;
      list.style.transition = '';
      onEnd?.();
    } else {
      this.#settling = true;
      const done = () => {
        list.removeEventListener('transitionend', done);
        this.#settling = false;
        onEnd?.();
      };
      list.addEventListener('transitionend', done, { once: true });
      list.style.transform = `translate3d(${-x}px,0,0)`;
      // filet de sécurité si transitionend ne se déclenche pas (position identique)
      setTimeout(() => {
        if (this.#settling) done();
      }, 700);
    }
  }

  #setAdaptiveHeight() {
    if (!this.adaptiveHeight) {
      this.style.removeProperty('--active-slide-height');
      return;
    }
    const active = this.#all[this.#index];
    if (!active) return;
    const inner = active.firstElementChild instanceof HTMLElement ? active.firstElementChild : active;
    const h = inner.offsetHeight;
    if (h) this.style.setProperty('--active-slide-height', `${h}px`);
    const img = active.querySelector('img');
    if (img && !img.complete) {
      img.addEventListener('load', () => this.#setAdaptiveHeight(), { once: true });
    }
  }

  /* ---------- état ---------- */

  /** index réel (0..count-1) */
  get current() {
    if (this.isLoop) return (((this.#index - this.#cloneCount) % this.count) + this.count) % this.count;
    return this.#index;
  }

  get currentPage() {
    if (this.isFade || this.isLoop) return this.current;
    if (this.centerMode && !this.trimSpace) return this.current;
    const pages = this.pageCount;
    if (this.#index >= this.#maxIndex) return pages - 1;
    return Math.min(pages - 1, Math.round(this.#index / this.perMove));
  }

  #updateActive() {
    const visibleStart = this.#index;
    const visibleEnd = this.isFade ? this.#index : this.#index + this.perPage - 1;
    this.#all.forEach((s, i) => {
      const visible = i >= visibleStart && i <= visibleEnd;
      const active = i === this.#index;
      s.classList.toggle('is-active', active);
      s.classList.toggle('is-visible', visible);
      if (!s.classList.contains('ms__slide--clone')) s.setAttribute('aria-hidden', visible ? 'false' : 'true');
      if (this.dataset.pauseVideos === 'true' && !visible) this.#deactivateVideos(s);
    });
    this.#setAdaptiveHeight();
    this.#updateControls();
    this.dispatchEvent(new CustomEvent('media-slider:change', { detail: { index: this.current } }));
  }

  #deactivateVideos(slide) {
    slide.querySelectorAll('internal-video-component').forEach((v) => {
      const action = v.dataset.actionOnInactive;
      if (action === 'pause') v.pause?.();
      else if (action === 'mute') v.mute?.();
    });
  }

  #updateControls() {
    const arrows = this.refs.arrows;
    if (arrows) {
      const prev = arrows.querySelector('.ms__arrow--prev');
      const next = arrows.querySelector('.ms__arrow--next');
      const atStart = !this.isLoop && this.#index <= 0;
      const atEnd = !this.isLoop && this.#index >= this.#maxIndex;
      if (prev instanceof HTMLButtonElement) prev.disabled = atStart;
      if (next instanceof HTMLButtonElement) next.disabled = atEnd;
    }
    const dots = this.refs.dots;
    if (dots) {
      const page = this.currentPage;
      dots.querySelectorAll('.ms__dot').forEach((d, i) => {
        d.classList.toggle('is-active', i === page);
        d.setAttribute('aria-current', i === page ? 'true' : 'false');
      });
    }
  }

  /* ---------- navigation publique ---------- */

  go(index, instant = false) {
    if (this.count < 2 || this.destroyed) return;
    if (this.isFade) {
      this.#index = ((index % this.count) + this.count) % this.count;
      this.#applyFade();
      this.#updateActive();
      this.#restartAutoplay();
      return;
    }
    if (this.isLoop) {
      this.#index = index;
      this.#moveTo(index, instant, () => this.#normalizeLoop());
    } else {
      this.#index = Math.min(Math.max(index, 0), this.#maxIndex);
      this.#moveTo(this.#index, instant);
    }
    this.#updateActive();
    this.#restartAutoplay();
  }

  /** Après une transition en loop, revient dans la zone des originaux sans animation */
  #normalizeLoop() {
    const n = this.count;
    const lo = this.#cloneCount;
    const hi = this.#cloneCount + n - 1;
    if (this.#index < lo) {
      this.#index += n;
      this.#moveTo(this.#index, true);
    } else if (this.#index > hi) {
      this.#index -= n;
      this.#moveTo(this.#index, true);
    }
    this.#updateActive();
  }

  next() {
    if (this.isFade) return this.go(this.#index + 1);
    if (this.isLoop) return this.go(this.#index + this.perMove);
    if (this.#index >= this.#maxIndex) return;
    this.go(Math.min(this.#index + this.perMove, this.#maxIndex));
  }

  previous() {
    if (this.isFade) return this.go(this.#index - 1);
    if (this.isLoop) return this.go(this.#index - this.perMove);
    if (this.#index <= 0) return;
    this.go(Math.max(this.#index - this.perMove, 0));
  }

  goToPage(p) {
    if (this.isFade) return this.go(p);
    if (this.isLoop) return this.go(this.#cloneCount + p);
    if (this.centerMode && !this.trimSpace) return this.go(p);
    const pages = this.pageCount;
    if (p >= pages - 1) return this.go(this.#maxIndex);
    this.go(p * this.perMove);
  }

  /** Va à la slide originale i (utilisé par l'éditeur de thème) */
  goToOriginal(i) {
    if (this.isLoop) return this.go(this.#cloneCount + i, true);
    if (this.isFade) return this.go(i);
    this.go(Math.min(i, this.#maxIndex), true);
  }

  /* ---------- autoplay ---------- */

  #startAutoplay() {
    this.#stopAutoplay();
    if (this.destroyed || !this.autoplay || this.#paused || this.#hovered || !this.#inViewport || document.hidden) return;
    this.#autoplayTimer = window.setInterval(() => {
      if (this.#drag) return;
      if (!this.isLoop && !this.isFade && this.#index >= this.#maxIndex) {
        this.go(0);
      } else if (this.isFade && this.#index >= this.count - 1) {
        this.go(0);
      } else {
        this.next();
      }
    }, this.interval);
  }

  #stopAutoplay() {
    if (this.#autoplayTimer) window.clearInterval(this.#autoplayTimer);
    this.#autoplayTimer = 0;
  }

  #restartAutoplay() {
    if (this.autoplay) this.#startAutoplay();
  }

  pause() {
    this.#paused = true;
    this.#stopAutoplay();
  }

  play() {
    this.#paused = false;
    this.#startAutoplay();
  }

  /* ---------- événements ---------- */

  #bind() {
    // Resize
    this.#resizeObserver = new ResizeObserver(() => this.#onResize());
    this.#resizeObserver.observe(this.refs.track);
    DESKTOP.addEventListener('change', this.#onBreakpoint);

    // Hover / focus → pause autoplay
    this.addEventListener('pointerenter', () => {
      this.#hovered = true;
      this.#stopAutoplay();
    });
    this.addEventListener('pointerleave', () => {
      this.#hovered = false;
      this.#startAutoplay();
    });
    this.addEventListener('focusin', () => this.#stopAutoplay());
    this.addEventListener('focusout', () => this.#startAutoplay());
    document.addEventListener('visibilitychange', this.#onVisibility);

    // Viewport
    this.#intersection = new IntersectionObserver(
      (entries) => {
        this.#inViewport = entries.some((e) => e.isIntersecting);
        if (this.#inViewport) this.#startAutoplay();
        else this.#stopAutoplay();
      },
      { threshold: 0.2 }
    );
    this.#intersection.observe(this);

    // Clavier
    this.refs.track.setAttribute('tabindex', '0');
    this.refs.track.addEventListener('keydown', (e) => {
      if (this.destroyed) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.next();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.previous();
      }
    });

    // Drag
    if (this.dragMode !== 'none' && !this.isFade) this.#bindDrag();

    // Éditeur de thème
    document.addEventListener('shopify:block:select', this.#onBlockSelect);

    // Images chargées → recalcul hauteur
    this.refs.list.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', () => this.#setAdaptiveHeight(), { once: true });
    });
  }

  #onVisibility = () => {
    if (document.hidden) this.#stopAutoplay();
    else this.#startAutoplay();
  };

  #onBreakpoint = () => {
    // perPage/gap changent → on reconstruit (clones) et on garde la slide courante
    if (this.destroyed) {
      this.#teardown();
      return;
    }
    const wasDestroyed = this.refs.root.classList.contains('ms--destroyed');
    const cur = wasDestroyed ? 0 : this.current;
    this.refs.root.classList.remove('ms--destroyed');
    this.#build();
    if (this.isLoop) this.#index = this.#cloneCount + cur;
    else this.#index = Math.min(cur, this.#maxIndex);
    this.#layout(true);
    this.#startAutoplay();
  };

  #onResize() {
    if (this.#drag || this.destroyed) return;
    this.#layout(true);
  }

  #onBlockSelect = (event) => {
    if (this.destroyed) return;
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target)) return;
    const slide = target.closest('.ms__slide');
    if (!slide || slide.classList.contains('ms__slide--clone')) return;
    const i = this.#originals.indexOf(/** @type {HTMLElement} */ (slide));
    if (i >= 0) {
      this.pause();
      this.goToOriginal(i);
    }
  };

  /* ---------- drag ---------- */

  #bindDrag() {
    const track = this.refs.track;
    track.addEventListener('pointerdown', (e) => {
      if (this.destroyed) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (e.target instanceof Element && e.target.closest('button, input, select, textarea')) return;
      this.#stopAutoplay();
      this.#drag = {
        id: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        start: this.#position,
        last: this.#position,
        lastT: performance.now(),
        v: 0,
        moved: false,
        axis: null,
      };
      this.refs.list.style.transition = 'none';
    });

    track.addEventListener('pointermove', (e) => {
      const d = this.#drag;
      if (!d || e.pointerId !== d.id) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      if (!d.axis) {
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
        d.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (d.axis === 'x') track.setPointerCapture(d.id);
      }
      if (d.axis !== 'x') return;
      e.preventDefault();
      d.moved = true;
      let x = d.start + dx;
      if (!this.isLoop) {
        // résistance aux bords
        const min = -this.#clampPosition(Infinity);
        const max = 0;
        if (x > max) x = max + (x - max) * 0.3;
        if (x < min) x = min + (x - min) * 0.3;
      }
      const now = performance.now();
      const dt = now - d.lastT || 16;
      d.v = (x - d.last) / dt;
      d.last = x;
      d.lastT = now;
      this.#position = x;
      this.refs.list.style.transform = `translate3d(${x}px,0,0)`;
      this.refs.root.classList.add('is-dragging');
    });

    const end = (e) => {
      const d = this.#drag;
      if (!d || e.pointerId !== d.id) return;
      this.#drag = null;
      this.refs.root.classList.remove('is-dragging');
      this.refs.list.style.transition = '';
      if (!d.moved) {
        this.#startAutoplay();
        return;
      }
      // Empêche le clic sur le lien après un drag
      const swallow = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
      };
      track.addEventListener('click', swallow, { capture: true, once: true });
      setTimeout(() => track.removeEventListener('click', swallow, { capture: true }), 300);

      if (this.dragMode === 'free') {
        this.#freeSettle(d.v);
      } else {
        this.#snapSettle(d);
      }
    };
    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);
    track.addEventListener('lostpointercapture', end);
  }

  #snapSettle(d) {
    const dx = this.#position - d.start;
    const fast = Math.abs(d.v) > 0.5;
    const threshold = this.#slideWidth * 0.2;
    const dir = dx < 0 ? 1 : -1;
    let target = this.#index;
    if (fast || Math.abs(dx) > threshold) {
      const moved = Math.max(1, Math.round(Math.abs(dx) / this.#step));
      target = this.#index + dir * Math.min(moved, this.perMove * 2);
    }
    if (this.isLoop) {
      this.#index = target;
      this.#moveTo(target, false, () => this.#normalizeLoop());
    } else {
      this.#index = Math.min(Math.max(target, 0), this.#maxIndex);
      this.#moveTo(this.#index);
    }
    this.#updateActive();
    this.#startAutoplay();
  }

  #freeSettle(v) {
    // inertie simple
    let x = this.#position + v * 260;
    if (this.isLoop) {
      // en loop on ramène dans la zone des originaux
      const n = this.count;
      const span = n * this.#step;
      const lo = -this.#positionOf(this.#cloneCount + n);
      const hi = -this.#positionOf(this.#cloneCount);
      while (x < lo) x += span;
      while (x > hi) x -= span;
    } else {
      x = -this.#clampPosition(-x);
    }
    this.#position = x;
    this.refs.list.style.transform = `translate3d(${x}px,0,0)`;
    // index = slide la plus proche
    const raw = this.centerMode ? (-x + (this.#contentWidth - this.#slideWidth) / 2) / this.#step : -x / this.#step;
    this.#index = Math.round(raw);
    if (!this.isLoop) this.#index = Math.min(Math.max(this.#index, 0), this.#all.length - 1);
    this.#updateActive();
    this.#startAutoplay();
  }
}

if (!customElements.get('media-slider-component')) {
  customElements.define('media-slider-component', MediaSliderComponent);
}
